'use client'

import { useCallback, useEffect } from 'react'
import { useBoot } from '@/app/(app)/context/BootContext'
import { appBridgeHasSessionToken, waitForAppBridge } from '@/app/(app)/lib/appBridgeSession'

export { useAppBridgeReady } from '@/app/(app)/context/AppBridgeReadyContext'

declare global {
  interface Window {
    shopify?: {
      idToken?: () => Promise<string>
      config?: { apiKey?: string }
      webVitals?: { onReport: (cb: (metrics: unknown) => void | Promise<void>) => Promise<void> }
    }
    __sigmaSenseWebVitalsHooked?: boolean
  }
}

const SESSION_TOKEN_CACHE_TTL_MS = 30 * 1000 // 30 seconds
const SESSION_TOKEN_TIMEOUT_MS = 8000
const CLIENT_DEBUG_LATENCY =
  process.env.NEXT_PUBLIC_DEBUG_LATENCY === '1' || process.env.NEXT_PUBLIC_SIGMA_PERF_LOG === '1'

/** Module-level cache for session token to avoid repeated idToken() calls within 30s. */
let tokenCache: { token: string; expiresAt: number } | null = null

function getCachedToken(): string | null {
  if (!tokenCache) return null
  if (Date.now() >= tokenCache.expiresAt) {
    tokenCache = null
    return null
  }
  return tokenCache.token
}

function setCachedToken(token: string): void {
  tokenCache = { token, expiresAt: Date.now() + SESSION_TOKEN_CACHE_TTL_MS }
}

/** Clear session token cache so the next request fetches a fresh token. Use after 401 to retry. */
export function clearSessionTokenCache(): void {
  tokenCache = null
}

async function fetchFreshSessionToken(): Promise<string> {
  const idToken = window.shopify?.idToken
  if (!idToken) throw new Error('App Bridge session token not available')
  return await Promise.race<string>([
    idToken(),
    new Promise<string>((_, reject) =>
      setTimeout(() => reject(new Error('Session token timeout')), SESSION_TOKEN_TIMEOUT_MS)
    ),
  ])
}

export function useAuthenticatedFetch() {
  const { bootHost, bootShop } = useBoot()
  const effectiveHost = bootHost
  const effectiveShop = bootShop

  const isDirectMode = !!effectiveShop && !effectiveHost

  const logFetchTrace = useCallback((event: string, meta: Record<string, unknown> = {}) => {
    if (!CLIENT_DEBUG_LATENCY) return
    // eslint-disable-next-line no-console
    console.log('[trace][fetch]', { event, ...meta })
  }, [])

  const directFetch = useCallback(
    async (uri: string, options: RequestInit = {}) => {
      const startedAt = Date.now()
      const headers = new Headers(options.headers || {})
      headers.set('X-Requested-With', 'XMLHttpRequest')
      const response = await fetch(uri, {
        ...options,
        headers,
        credentials: 'include',
      })
      logFetchTrace('direct_done', {
        uri,
        method: options.method ?? 'GET',
        status: response.status,
        durationMs: Date.now() - startedAt,
        traceId: response.headers.get('x-sigma-trace-id'),
        serverTiming: response.headers.get('server-timing'),
      })
      return response
    },
    [logFetchTrace]
  )

  const embeddedFetch = useCallback(
    async (uri: string, options: RequestInit = {}) => {
      const startedAt = Date.now()
      const tokenStarted = Date.now()
      await waitForAppBridge()

      if (!appBridgeHasSessionToken()) {
        throw new Error('App Bridge session token not available')
      }

      let token = getCachedToken()
      if (!token) {
        token = await fetchFreshSessionToken()
        setCachedToken(token)
        logFetchTrace('session_token_fresh', { durationMs: Date.now() - tokenStarted })
      }

      const method = (options.method ?? 'GET').toUpperCase()

      const doFetch = (bearer: string) => {
        const headers = new Headers(options.headers || {})
        headers.set('Authorization', `Bearer ${bearer}`)
        headers.set('X-Requested-With', 'XMLHttpRequest')
        return fetch(uri, { ...options, headers })
      }

      let response = await doFetch(token)
      // Retry once with a fresh token on 401 (cached tokens expire; GET used to be the only retry).
      if (response.status === 401) {
        clearSessionTokenCache()
        logFetchTrace('embedded_401_retry', { uri, method })
        try {
          const fresh = await fetchFreshSessionToken()
          setCachedToken(fresh)
          response = await doFetch(fresh)
        } catch {
          /* return original 401 path */
        }
      }

      logFetchTrace('embedded_done', {
        uri,
        method,
        status: response.status,
        durationMs: Date.now() - startedAt,
        traceId: response.headers.get('x-sigma-trace-id'),
        serverTiming: response.headers.get('server-timing'),
      })
      return response
    },
    [logFetchTrace]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      if (
        process.env.NODE_ENV === 'development' &&
        window.shopify?.webVitals &&
        !window.__sigmaSenseWebVitalsHooked
      ) {
        window.__sigmaSenseWebVitalsHooked = true
        window.shopify.webVitals.onReport((metrics: unknown) => {
          // eslint-disable-next-line no-console
          console.log('[Sigma Sense Web Vitals]', metrics)
        })
      }
    } catch {
      // Ignore Web Vitals setup failures
    }
  }, [])

  return isDirectMode ? directFetch : embeddedFetch
}
